import { Button } from "@/components/Primitives/Button"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/Primitives/Card"
import { Input } from "@/components/Primitives/Input"
import { Label } from "@/components/Primitives/Label"
import { useConfig } from "@/hooks/useConfig"
import { useState } from "react"
import { Eye, EyeClosed } from "lucide-react"
import { Link } from "react-router-dom"
import { MarkdownRenderer } from "@/components/Primitives/MarkdownRenderer"

import { FormErrorDisplay } from "@/components/Primitives/FormErrorDisplay"
import { GoogleOAuthButton } from "@/components/Complex/GoogleOAuthButton"
import { Text } from "@/components/Primitives/Text"

interface LoginFormProps extends React.ComponentPropsWithoutRef<"div"> {
  email: string
  password: string
  onEmailChange: (email: string) => void
  onPasswordChange: (password: string) => void
  onSubmit: () => Promise<void>
  nextPathName: string
  fieldErrors: {
    email?: string
    password?: string
  }
  isSubmitting?: boolean
  error?: string
}

export function LoginForm({
  email,
  password,
  onEmailChange,
  onPasswordChange,
  onSubmit,
  fieldErrors = {},
  isSubmitting = false,
  error,
  nextPathName,
}: LoginFormProps) {
  const { t, tp } = useConfig()
  const [showPassword, setShowPassword] = useState(false)
  const signUpParagraph = tp("config_login_sign_up", { url: "/signup" })
  const isFormValid = password.length > 0
  const fieldLabels = {
    email: t("config_login_email"),
    password: t("config_login_password"),
  }
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isFormValid) {
      await onSubmit()
    }
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("config_login_title")}</CardTitle>
        <Text className="text-neutral-700 text-sm">
          {t("config_login_description")}
        </Text>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} noValidate>
          <div className="flex flex-col gap-4">
            <div className="grid gap-2">
              <Label htmlFor="email">{t("config_login_email")}</Label>
              <Input
                id="email"
                type="email"
                data-testid="email"
                placeholder={t("config_login_email_placeholder")}
                value={email}
                required
                error={fieldErrors.email}
                onChange={(event) => {
                  onEmailChange(event.target.value)
                }}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="password">{t("config_login_password")}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  data-testid="password"
                  placeholder={t("config_login_password_placeholder")}
                  value={password}
                  required
                  error={fieldErrors.password}
                  onChange={(event) => onPasswordChange(event.target.value)}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeClosed className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
            <FormErrorDisplay
              fieldErrors={fieldErrors}
              fieldLabels={fieldLabels}
            />
            <div className="flex flex-col jusfify-center md:items-center gap-4 md:flex-row">
              <Link
                to="/login/forgot"
                className="text-sm text-purple-500 md:text-center hover:text-purple-700 font-semibold px-4 py-2 md:w-1/2 h-fit"
              >
                {t("config_login_forgot_password")}
              </Link>

              <Button
                type="submit"
                className="w-full md:w-1/2 h-fit"
                data-testid="submit"
                disabled={!isFormValid || isSubmitting}
              >
                {isSubmitting
                  ? t("config_login_button_submitting")
                  : t("config_login_button_submit")}
              </Button>
            </div>
            {error && !Object.values(fieldErrors).some(Boolean) && (
              <p className="text-sm text-red-500 text-center">{error}</p>
            )}
          </div>
        </form>
        <GoogleOAuthButton navigateUrl={nextPathName} />
      </CardContent>
      <CardFooter>
        <MarkdownRenderer
          variant="small"
          className="[&>p]:mt-0"
          content={signUpParagraph}
        />
      </CardFooter>
    </Card>
  )
}
